import axios from "axios";

const ENV_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve the backend base URL at runtime.
 *
 * When the user visits the **production** custom domain (e.g. amdsdiary.org),
 * the build-time `REACT_APP_BACKEND_URL` still points at the preview backend
 * because the env var is baked at build time. Calling the preview backend
 * from amdsdiary.org makes every request a third-party cross-origin call,
 * which Safari ITP and Firefox Total Cookie Protection silently block.
 *
 * Workaround: if we're being served from anywhere other than the preview
 * subdomain, talk to the **current origin** instead. Emergent's deployment
 * routes `/api/*` on the production host (and any linked custom domain) to
 * the same backend, so this lands first-party and cookies flow correctly.
 */
function resolveBackendUrl() {
  if (typeof window === "undefined") return ENV_URL;
  const here = window.location.origin;
  // Preview is always *.preview.emergentagent.com — keep using the env var.
  if (here.includes(".preview.emergentagent.com")) return ENV_URL;
  // Localhost dev — keep env var (CRA proxy or explicit URL).
  if (here.startsWith("http://localhost") || here.startsWith("http://127.")) return ENV_URL;
  // Otherwise we're on a custom domain / emergent.host production — call
  // ourselves so cookies are first-party.
  return here;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function isPlatinumRequired(error) {
  const d = error?.response?.data?.detail;
  return error?.response?.status === 402
    || (d && typeof d === "object" && d.error === "platinum_required");
}

export default api;
